import {
  useCallback,
  useEffect,
  useState,
  type ChangeEvent,
} from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Check,
  Close,
  Notifications,
  NotificationsActive,
  NotificationsOff,
  PhoneInTalk,
  Campaign,
  AccountBalanceWallet,
  Timer,
  Hub,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import LinkPagination from './LinkPagination';
import { ALERTS_DRAWER_OPEN_EVENT } from '../constants/alertDrawerEvents';
import { fetchAlerts, fetchAlertsUnreadCount, markAlertsAsRead } from '../services/alerts';
import {
  fetchWebPushStatus,
  getBrowserPushPermission,
  isWebPushSupported,
  subscribeToWebPush,
  unsubscribeFromWebPush,
  type BrowserPushPermission,
  type BrowserPushStatus,
} from '../services/pushNotifications';
import { useAuth } from '../contexts/AuthContext';
import { useListSearchParams } from '../hooks/useListSearchParams';
import useTenantAlertsWebSocket from '../hooks/useTenantAlertsWebSocket';
import type { Alert } from '../types';
import { formatDateTime } from '../utils/date';
import { useNumberFormat } from '../hooks/useNumberFormat';

const DEFAULT_PAGE_SIZE = 30;
const PAGE_PARAM = 'alerts_page';
const PAGE_SIZE_PARAM = 'alerts_page_size';

const buildTranslationParams = (messageParams?: Record<string, unknown>) =>
  (messageParams ?? {}) as Record<string, string | number | boolean>;

const getAlertIcon = (subjectType: string) => {
  const iconProps = { fontSize: 'small' as const, sx: { color: 'text.secondary' } };
  switch (subjectType) {
    case 'call':
      return <PhoneInTalk {...iconProps} />;
    case 'contact_z_connection':
      return <Hub {...iconProps} />;
    case 'campaign':
      return <Campaign {...iconProps} />;
    case 'subscription':
      return <AccountBalanceWallet {...iconProps} />;
    case 'minute_pack':
      return <Timer {...iconProps} />;
    default:
      return <Notifications {...iconProps} />;
  }
};

export default function AlertsDrawer() {
  const { t, i18n } = useTranslation(['common', 'notifications']);
  const { locale } = useNumberFormat();
  const { currentTenant } = useAuth();
  const {
    page: alertsPage,
    rowsPerPage: alertsPageSize,
    getPageLink,
    setPage: setAlertsPage,
    setRowsPerPage: setAlertsPageSize,
  } = useListSearchParams({
    pageParam: PAGE_PARAM,
    pageSizeParam: PAGE_SIZE_PARAM,
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerAlerts, setDrawerAlerts] = useState<Alert[]>([]);
  const [totalAlerts, setTotalAlerts] = useState(0);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [unreadCount, setUnreadCount] = useState<number | null>(null);
  const [shouldReloadDrawer, setShouldReloadDrawer] = useState(false);
  const [pushStatus, setPushStatus] = useState<BrowserPushStatus | null>(null);
  const [pushPermission, setPushPermission] = useState<BrowserPushPermission>(
    getBrowserPushPermission(),
  );
  const [pushLoading, setPushLoading] = useState(false);
  const [pushError, setPushError] = useState<string | null>(null);

  const fetchUnread = useCallback(async () => {
    if (!currentTenant) {
      setUnreadCount(null);
      return;
    }
    try {
      const count = await fetchAlertsUnreadCount();
      setUnreadCount(count);
    } catch (error) {
      console.error('Failed to load unread alert count', error);
      setUnreadCount(null);
    }
  }, [currentTenant]);

  const reloadDrawer = useCallback(async () => {
    if (!currentTenant) {
      setDrawerAlerts([]);
      setTotalAlerts(0);
      return;
    }
    setDrawerLoading(true);
    try {
      const data = await fetchAlerts({ page: alertsPage + 1, pageSize: alertsPageSize });
      setDrawerAlerts(data.results);
      setTotalAlerts(data.count);
    } catch (error) {
      console.error('Failed to load paginated alerts', error);
      setDrawerAlerts([]);
      setTotalAlerts(0);
    } finally {
      setDrawerLoading(false);
    }
  }, [currentTenant, alertsPage, alertsPageSize]);

  const reloadPushStatus = useCallback(async () => {
    setPushPermission(getBrowserPushPermission());
    if (!currentTenant) {
      setPushStatus(null);
      setPushError(null);
      return;
    }
    try {
      const statusData = await fetchWebPushStatus();
      setPushStatus(statusData);
      setPushError(null);
    } catch (error) {
      console.error('Failed to load Web Push status', error);
      setPushStatus(null);
    }
  }, [currentTenant]);

  const handleMarkAllAsRead = useCallback(async () => {
    if (drawerAlerts.length === 0) {
      return;
    }
    const unreadIds = drawerAlerts.filter((alert) => !alert.isRead).map((alert) => alert.id);
    if (unreadIds.length === 0) {
      return;
    }

    setMarkingAll(true);
    try {
      const marked = await markAlertsAsRead(unreadIds);
      if (marked > 0) {
        setDrawerAlerts((prev) =>
          prev.map((alert) =>
            unreadIds.includes(alert.id) ? { ...alert, isRead: true, readAt: new Date().toISOString() } : alert,
          ),
        );
        setUnreadCount((prev) => Math.max(0, (prev ?? 0) - marked));
      }
    } catch (error) {
      console.error('Failed to mark all alerts as read', error);
    } finally {
      setMarkingAll(false);
    }
  }, [drawerAlerts]);

  const handleIncomingAlert = useCallback((alert: Alert) => {
    setUnreadCount((previous) => (previous ?? 0) + (alert.isRead ? 0 : 1));
    setShouldReloadDrawer(true);
  }, []);

  useTenantAlertsWebSocket({ onAlert: handleIncomingAlert });

  useEffect(() => {
    fetchUnread();
  }, [fetchUnread]);

  useEffect(() => {
    if (!drawerOpen) {
      return;
    }
    void reloadPushStatus();
  }, [drawerOpen, reloadPushStatus]);

  useEffect(() => {
    if (!drawerOpen) {
      return undefined;
    }

    let canceled = false;
    const load = async () => {
      await reloadDrawer();
      if (!canceled) {
        setShouldReloadDrawer(false);
      }
    };

    void load();
    return () => {
      canceled = true;
    };
  }, [drawerOpen, reloadDrawer, shouldReloadDrawer]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => setDrawerOpen(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleExternalOpen = () => {
      setDrawerOpen(true);
    };

    window.addEventListener(ALERTS_DRAWER_OPEN_EVENT, handleExternalOpen);
    return () => {
      window.removeEventListener(ALERTS_DRAWER_OPEN_EVENT, handleExternalOpen);
    };
  }, []);

  const handleMarkAlertAsRead = useCallback(
    async (alertId: string) => {
      try {
        const marked = await markAlertsAsRead([alertId]);
        if (marked <= 0) {
          return;
        }
        setDrawerAlerts((prev) =>
          prev.map((alert) =>
            alert.id === alertId ? { ...alert, isRead: true, readAt: new Date().toISOString() } : alert,
          ),
        );
        setUnreadCount((prev) => Math.max(0, (prev ?? 0) - marked));
      } catch (error) {
        console.error('Failed to mark alert as read', error);
      }
    },
    [],
  );

  const handlePageChange = useCallback(
    (_event: unknown, newPage: number) => {
      setAlertsPage(newPage);
    },
    [setAlertsPage],
  );

  const handleRowsPerPageChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setAlertsPageSize(Number(event.target.value));
    },
    [setAlertsPageSize],
  );

  const handleEnablePush = useCallback(async () => {
    setPushLoading(true);
    setPushError(null);
    try {
      const statusData = await subscribeToWebPush(i18n.language);
      setPushStatus(statusData);
      setPushPermission(getBrowserPushPermission());
    } catch (error) {
      console.error('Failed to enable Web Push notifications', error);
      setPushPermission(getBrowserPushPermission());
      const message = error instanceof Error ? error.message : 'web_push_failed';
      setPushError(message);
    } finally {
      setPushLoading(false);
    }
  }, [i18n.language]);

  const handleDisablePush = useCallback(async () => {
    setPushLoading(true);
    setPushError(null);
    try {
      const statusData = await unsubscribeFromWebPush();
      setPushStatus(statusData);
      setPushPermission(getBrowserPushPermission());
    } catch (error) {
      console.error('Failed to disable Web Push notifications', error);
      const message = error instanceof Error ? error.message : 'web_push_failed';
      setPushError(message);
    } finally {
      setPushLoading(false);
    }
  }, []);

  const hasDrawerUnread = drawerAlerts.some((alert) => !alert.isRead);
  const badgeContent = (unreadCount ?? 0) > 0 ? unreadCount : undefined;
  const showPushPanel = Boolean(pushStatus?.enabled || pushStatus?.subscribed);
  const pushSupported = isWebPushSupported();
  const pushUnavailable = !pushStatus?.configured || !pushSupported || pushPermission === 'denied';
  const pushLabelKey = (() => {
    if (!pushSupported) {
      return 'common:alerts.push.unsupported';
    }
    if (!pushStatus?.configured) {
      return 'common:alerts.push.unavailable';
    }
    if (pushPermission === 'denied') {
      return 'common:alerts.push.denied';
    }
    if (pushStatus?.subscribed) {
      return 'common:alerts.push.enabled';
    }
    return 'common:alerts.push.disabled';
  })();
  const pushErrorMessage = (() => {
    switch (pushError) {
      case 'web_push_unsupported':
        return t('common:alerts.push.errors.web_push_unsupported');
      case 'web_push_unavailable':
        return t('common:alerts.push.errors.web_push_unavailable');
      case 'web_push_denied':
        return t('common:alerts.push.errors.web_push_denied');
      case 'web_push_dismissed':
        return t('common:alerts.push.errors.web_push_dismissed');
      case 'web_push_failed':
        return t('common:alerts.push.errors.web_push_failed');
      default:
        return pushError ? t('common:alerts.push.errors.web_push_failed') : '';
    }
  })();

  return (
    <>
      <IconButton
        size="large"
        color="inherit"
        aria-label={t('common:alerts.iconLabel')}
        sx={{ m: 0 }}
        onClick={handleDrawerOpen}
      >
        <Badge badgeContent={badgeContent} color="error" max={99}>
          <Notifications color="info" />
        </Badge>
      </IconButton>

      <Drawer anchor="right" open={drawerOpen} onClose={handleDrawerClose}>
        <Box
          sx={{
            width: { xs: '90vw', sm: 420 },
            maxWidth: 480,
            minHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Notifications sx={{ color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="h6">{t('common:alerts.drawerTitle')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                size="small"
                color="inherit"
                startIcon={markingAll ? <CircularProgress size={16} /> : <Check fontSize="small" />}
                onClick={handleMarkAllAsRead}
                disabled={!hasDrawerUnread || drawerLoading || markingAll}
                sx={{ textTransform: 'none' }}
              >
                {t('common:alerts.markAll')}
              </Button>
              <IconButton onClick={handleDrawerClose}>
                <Close />
              </IconButton>
            </Box>
          </Box>
          <Divider />
          {showPushPanel && (
            <>
              <Box
                sx={{
                  px: 2,
                  py: 1.25,
                  display: 'flex',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: 1.5,
                  flexDirection: { xs: 'column', sm: 'row' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  {pushStatus?.subscribed ? (
                    <NotificationsActive sx={{ fontSize: 20, color: 'success.main', mt: 0.25 }} />
                  ) : (
                    <NotificationsOff sx={{ fontSize: 20, color: 'text.secondary', mt: 0.25 }} />
                  )}
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {t('common:alerts.push.title')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="div">
                      {t(pushLabelKey)}
                    </Typography>
                    {pushError && (
                      <Typography variant="caption" color="error" component="div">
                        {pushErrorMessage}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Button
                  size="small"
                  variant={pushStatus?.subscribed ? 'outlined' : 'contained'}
                  color={pushStatus?.subscribed ? 'inherit' : 'primary'}
                  disabled={pushLoading || pushUnavailable}
                  onClick={pushStatus?.subscribed ? handleDisablePush : handleEnablePush}
                  startIcon={pushLoading ? <CircularProgress size={16} /> : undefined}
                  sx={{ textTransform: 'none', minWidth: 112 }}
                >
                  {pushStatus?.subscribed
                    ? t('common:alerts.push.disable')
                    : t('common:alerts.push.enable')}
                </Button>
              </Box>
              <Divider />
            </>
          )}
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {drawerLoading && (
              <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {!drawerLoading && drawerAlerts.length === 0 && (
              <Box sx={{ p: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('common:alerts.empty')}
                </Typography>
              </Box>
            )}
            {!drawerLoading && drawerAlerts.length > 0 && (
              <List disablePadding>
                {drawerAlerts.map((alert) => {
                  const title = t(alert.titleKey);
                  const message = t(alert.messageKey, buildTranslationParams(alert.messageParams));
                  const hasLink = Boolean(alert.linkPath);
                  return (
                    <ListItem
                      key={alert.id}
                      disablePadding
                      sx={{
                        px: 2,
                        py: 1.25,
                        backgroundColor: alert.isRead ? 'inherit' : 'action.selected',
                        display: 'flex',
                        alignItems: 'stretch',
                      }}
                    >
                      <ListItemButton
                        component={hasLink ? RouterLink : 'div'}
                        to={hasLink ? alert.linkPath : undefined}
                        onClick={() => {
                          if (!alert.isRead) {
                            handleMarkAlertAsRead(alert.id);
                          }
                          if (hasLink) {
                            handleDrawerClose();
                          }
                        }}
                        sx={{ pr: 2, alignItems: 'flex-start', flex: 1 }}
                      >
                        <ListItemIcon sx={{ minWidth: 32, mt: 0.25 }}>
                          {getAlertIcon(alert.subjectType)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="subtitle2" fontWeight={alert.isRead ? 500 : 700}>
                              {title}
                            </Typography>
                          }
                          secondary={
                            <>
                              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
                                {message}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" component="div">
                                {formatDateTime(alert.createdAt, {
                                  locale,
                                  options: {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  },
                                })}
                              </Typography>
                            </>
                          }
                          slotProps={{
                            secondary: {
                              component: 'div',
                            },
                          }}
                          sx={{ whiteSpace: 'normal', marginTop: 0 }}
                        />
                      </ListItemButton>
                      <Tooltip title={t('common:alerts.markAsRead')} arrow>
                        <Box
                          component="span"
                          sx={{ display: 'inline-flex', mr: 1, alignSelf: 'flex-start', mt: 0.5 }}
                        >
                          <IconButton
                            aria-label={t('common:alerts.markAsRead')}
                            disabled={alert.isRead}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleMarkAlertAsRead(alert.id);
                            }}
                          >
                            <Check fontSize="small" />
                          </IconButton>
                        </Box>
                      </Tooltip>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>

          <Divider />
          <Box sx={{ px: 2, py: 1 }}>
            <LinkPagination
              component="div"
              sx={{
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-select': {
                  display: 'none',
                },
              }}
              count={totalAlerts}
              page={alertsPage}
              rowsPerPage={alertsPageSize}
              rowsPerPageOptions={[30]}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              labelRowsPerPage=""
              getPageLink={getPageLink}
              showFirstButton
              showLastButton
            />
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
